/**
 * motion.js — shared motion baseline (Story 1.6).
 *
 * The single JS-motion utility every section imports. It is NOT an island and
 * defines NO custom element — it is the shared brain that later sections
 * (Motion Hero 2.1, PLP/PDP reveals, etc.) import via the `motion` specifier.
 *
 * Governing invariants:
 *  - AD-5  CSS owns hover/fade/color; the reveal system below only DETECTS
 *          scroll position (native `IntersectionObserver`, no library at all —
 *          GSAP/ScrollTrigger were removed 2026-08-25, see the "IO swap" note
 *          on `initScrollReveals` below) and flips an attribute for CSS to paint.
 *  - AD-6  Native scroll only. No scroller override, no pin/scrub hijack,
 *          no smooth-scroll library (Lenis excluded).
 *  - AD-7  Poster-first video: the poster is the LCP element; the video source
 *          lazy-loads only after first render; reduced-motion loads NO video.
 *  - AD-8  Framework-free — no Alpine/React/etc.
 *  - AD-13 Build-less: no bundler, no new asset file, no re-vendoring. Anything
 *          added here has to be plain ES modules over the existing import map.
 *  - AD-15 60fps, budget-respecting; motion is progressive enhancement, so
 *          content is visible/usable without JS (reveals animate from the
 *          end-state, they do not gate content behind JS).
 *
 * Every JS motion path is gated on `prefers-reduced-motion` via
 * `window.matchMedia()`, checked live (not cached) at bind time; a global CSS
 * `@media (prefers-reduced-motion: reduce)` rule in critical.css is the
 * belt-and-braces safety net.
 */

/** The reduced-motion media condition, shared by the JS gate and probes. */
export const REDUCE_MOTION = '(prefers-reduced-motion: reduce)';

/** True when the user asked for reduced motion (checked live, not cached). */
export function prefersReducedMotion() {
  return window.matchMedia(REDUCE_MOTION).matches;
}

/**
 * The default ScrollTrigger `start` for a reveal.
 *
 * Declared ONCE because it is read in two places that must never drift apart —
 * the trigger that is created, and `isPastStart()`'s "would that trigger already
 * have fired?" geometry check. Two literals is how those two silently disagree.
 *
 * `top 88%` = fire when the element's top rises to 88% of the viewport height,
 * i.e. while it is still entering the bottom edge. Tuned earlier than the
 * previous `top 85%` on purpose: the reveal should be UNDER WAY as the element
 * arrives, so the visitor perceives it settling into place. Anything later
 * (`top 75%`, and certainly `bottom bottom`) animates content that is already
 * fully on screen, which reads as a delayed pop rather than an entrance.
 */
const DEFAULT_REVEAL_START = 'top 88%';

/**
 * Reveal roles — the three treatments `critical.css` paints (Story 2.1 fade
 * replaced 2026-08-04). A section may declare one directly as the attribute's
 * value (`data-reveal="media"`); anything else, including the bare `data-reveal`
 * that every current consumer ships, is inferred by `inferRevealRole()`.
 */
const REVEAL_ROLES = ['text', 'media', 'card'];

/** Selector for "this element is fundamentally a picture, not prose". */
const MEDIA_SELECTOR = 'img, picture, video, svg, [data-poster-video]';

/**
 * Stagger geometry. 80ms is far enough apart to read as a sequence and close
 * enough that the group still feels like one gesture; the 320ms ceiling is the
 * point past which a late card reads as broken rather than choreographed — it
 * matters for a 12-card carousel, where an uncapped ramp would hold the last
 * card back by nearly a second after its neighbours had settled.
 */
const REVEAL_STAGGER_STEP = 0.08;
const REVEAL_STAGGER_MAX = 0.32;

/**
 * How far up the tree we look for the box a reveal group actually shares.
 * Bounded so an unexpected markup shape degrades to "no stagger" rather than
 * grouping half a page together.
 */
const REVEAL_GROUP_HOP_LIMIT = 3;

/**
 * Which of the three roles is `el`?
 *
 * The point of roles is that a headline, a photograph and a product card should
 * NOT share one animation — identical timing on unlike content is what makes a
 * reveal read as a stock plugin (docs/05-design-direction.md: the client is
 * buying "smooth micro-motion" as the differentiator). CSS cannot ask "does this
 * element contain a picture?", so the classification happens here, at bind time,
 * and is published as a `data-reveal-role` attribute for CSS to key off.
 *
 * Inference order matters, and is content-shaped rather than tag-shaped:
 *  - picture AND words  ⇒ `card`  — a tile/product card/trust item: an image
 *    with a label attached. (Tag-shaped rules fail here: trust-item leads with
 *    an <h3>, product-card has no heading at all, and both are cards.)
 *  - picture, no words  ⇒ `media` — a bare image or video block.
 *  - words, no picture  ⇒ `text`  — a headline/copy/CTA block.
 *
 * Deliberately NOT mutating `data-reveal` itself: that attribute is the
 * section's, and section CSS selects on it.
 *
 * @param {Element} el
 * @returns {'text'|'media'|'card'}
 */
function inferRevealRole(el) {
  const declared = el.dataset.reveal;
  if (declared && REVEAL_ROLES.includes(declared)) return declared;

  const hasMedia = el.matches(MEDIA_SELECTOR) || el.querySelector(MEDIA_SELECTOR) !== null;
  if (!hasMedia) return 'text';

  // `textContent` of an <svg> icon can be non-empty (a <title> child), but an
  // icon-plus-label item is a card either way, so that case needs no special
  // handling — it lands on the answer it should.
  const hasText = el.textContent.trim().length > 0;
  return hasText ? 'card' : 'media';
}

/**
 * The box whose children form one reveal group, for stagger purposes.
 *
 * Not simply `el.parentElement`, because sibling reveals are routinely NOT DOM
 * siblings: Shopify wraps every `content_for 'blocks'` child in its own
 * `.shopify-block`, and the carousels wrap each product card in its own `<li>`.
 * Climbing past any wrapper that holds a single element child collapses both
 * shapes generically, so tiles, trust items and carousel cards all resolve to
 * the one grid/row/track they visually belong to.
 *
 * Bounded by `REVEAL_GROUP_HOP_LIMIT` and stopped at the section boundary, so an
 * unforeseen chain of single-child wrappers cannot walk up to <body> and make
 * the whole page one group.
 *
 * @param {Element} el
 * @returns {Element|null}
 */
function revealGroupParent(el) {
  let parent = el.parentElement;
  for (let hop = 0; hop < REVEAL_GROUP_HOP_LIMIT; hop += 1) {
    if (!parent || parent.children.length !== 1) break;
    const next = parent.parentElement;
    // Never hop INTO the section wrapper: at that point "the group" would mean
    // "everything in the section", which is not a visual row.
    if (!next || next.classList.contains('shopify-section')) break;
    parent = next;
  }
  return parent;
}

/**
 * Fractions of a box that ScrollTrigger's positional keywords resolve to.
 * (Horizontal keywords included for completeness; this helper only ever gets
 * handed vertical starts, but an unrecognised token is handled safely anyway.)
 */
const START_KEYWORD_FRACTIONS = { top: 0, center: 0.5, bottom: 1, left: 0, right: 1 };

/**
 * Resolve one half of a ScrollTrigger `start` string ("top", "85%", "-100px")
 * into a pixel offset within a box of `size` px. Returns null when the token is
 * not one of the plain forms we can evaluate ourselves (e.g. `bottom-=100`,
 * `max`, a function) — the caller then declines to guess.
 */
function resolveStartOffset(token, size) {
  if (Object.prototype.hasOwnProperty.call(START_KEYWORD_FRACTIONS, token)) {
    return START_KEYWORD_FRACTIONS[token] * size;
  }
  const percent = /^(-?\d*\.?\d+)%$/.exec(token);
  if (percent) return (parseFloat(percent[1]) / 100) * size;
  const pixels = /^(-?\d*\.?\d+)(?:px)?$/.exec(token);
  if (pixels) return parseFloat(pixels[1]);
  return null;
}

/**
 * Convert a ScrollTrigger `start` string into an `IntersectionObserver`
 * `rootMargin` that fires at (as near as an IO can get to) the same scroll
 * position — the detection swap this whole module exists to make (GSAP
 * ScrollTrigger removed 2026-08-25; see the module header).
 *
 * The two systems test different things. ScrollTrigger's `start` names a
 * POINT ON THE ELEMENT ("top", "center", 88%-down-its-own-height, …) and asks
 * when that point crosses a point in the viewport. `IntersectionObserver`
 * (at the default `threshold: 0` this helper relies on) only ever asks "has
 * ANY part of the target entered the root's box" — for an element taller than
 * the remaining viewport, that first-entering part is always its OWN TOP EDGE,
 * i.e. exactly the `top` case. So the two are mathematically identical
 * whenever the element-side token is `top` (fraction 0) — which is the only
 * token this theme's one caller (`DEFAULT_REVEAL_START = 'top 88%'`) ever
 * passes (confirmed: no other `initScrollReveals` call in the theme supplies
 * a custom `start`; docs/js-contracts.md's file-header task grepped for it).
 *
 * The derivation, general for any element-fraction `eA` and viewport-fraction
 * `eV` (both in [0,1], `size`/`viewportHeight` in px):
 *
 *   ScrollTrigger fires when   rect.top + eA*elementHeight <= eV*viewportHeight
 *   IO (threshold 0) fires when rect.top <= viewportHeight + marginBottom
 *   Equating the two:          marginBottom = (eV - 1)*viewportHeight - eA*elementHeight
 *
 * For `'top 88%'`: eA = 0, eV = 0.88, so
 *   marginBottom = (0.88 - 1) * viewportHeight = -0.12 * viewportHeight = "-12%"
 * — expressed as a PERCENT string (not a frozen px number) whenever the
 * viewport-side token itself was a percent, so the trigger line stays
 * proportional to the viewport across a resize, exactly like ScrollTrigger's
 * own resize-triggered re-measure keeps a percent `start` proportional. A
 * viewport-side px token (`'top 100px'`) instead produces a fixed px
 * `marginBottom`, again mirroring `resolveStartOffset`'s own percent-vs-px
 * unit handling for `isPastStart`.
 *
 * A non-`top` element-side token (`eA !== 0`) cannot be expressed as a pure
 * percentage — it needs the element's own height baked in — so it falls back
 * to a one-time px computation using `elementHeight` (the caller's
 * already-measured `getBoundingClientRect().height`, batched in the same read
 * pass `isPastStart` uses). That value is frozen at bind time rather than
 * resize-reactive; nothing in this theme exercises that path today.
 *
 * Returns `null` for anything `resolveStartOffset` itself declines to parse
 * (a relative `+=`/`-=` offset, `max`, a function) — same "decline to guess"
 * contract `isPastStart` already has. The caller falls back to
 * `DEFAULT_REVEAL_START`'s own margin in that case.
 *
 * @param {string} start
 * @param {number} elementHeight - px, from a pre-measured `getBoundingClientRect()`
 * @param {number} viewportHeight - px
 * @returns {string|null} a `rootMargin` value, or null if `start` is unparseable
 */
function startToRootMargin(start, elementHeight, viewportHeight) {
  if (typeof start !== 'string') return null;
  const parts = start.trim().toLowerCase().split(/\s+/);
  if (parts.length !== 2) return null;

  const [elementToken, viewportToken] = parts;
  const elementFraction = Object.prototype.hasOwnProperty.call(START_KEYWORD_FRACTIONS, elementToken)
    ? START_KEYWORD_FRACTIONS[elementToken]
    : /^(-?\d*\.?\d+)%$/.exec(elementToken)
      ? parseFloat(/^(-?\d*\.?\d+)%$/.exec(elementToken)[1]) / 100
      : null;
  if (elementFraction === null && !/^(-?\d*\.?\d+)(?:px)?$/.test(elementToken)) return null;

  // `top` (or an explicit `0%`) is the only case a pure percentage can express —
  // see the doc comment above. Everything else needs the frozen px form.
  if (elementFraction === 0) {
    const percentMatch = /^(-?\d*\.?\d+)%$/.exec(viewportToken);
    if (percentMatch) {
      const eV = parseFloat(percentMatch[1]);
      return `0px 0px ${(eV - 100).toFixed(4)}% 0px`;
    }
  }

  const elementOffset = resolveStartOffset(elementToken, elementHeight);
  const viewportOffset = resolveStartOffset(viewportToken, viewportHeight);
  if (elementOffset === null || viewportOffset === null) return null;

  const marginBottomPx = viewportOffset - elementOffset - viewportHeight;
  return `0px 0px ${Math.round(marginBottomPx)}px 0px`;
}

/**
 * Fallback `rootMargin` for a `start` string `startToRootMargin` cannot parse
 * (the same class of value `isPastStart` already declines to guess about —
 * conservative-by-construction, not a hardcoded `88`). Derived by CALLING
 * `startToRootMargin` with `DEFAULT_REVEAL_START` rather than writing the
 * resulting percentage as a literal, so the two can never drift apart if
 * `DEFAULT_REVEAL_START` ever changes. The element/viewport-height arguments
 * are unused on this path — `DEFAULT_REVEAL_START`'s `top` element-token
 * takes the pure-percentage branch above, which needs neither.
 */
const DEFAULT_ROOT_MARGIN = startToRootMargin(DEFAULT_REVEAL_START, 0, 0);

/**
 * Has `el` ALREADY scrolled past `start` at the moment we are binding it?
 * (Story 2.2 — closes the deferred-work gap opened in 2.1.)
 *
 * `initScrollReveals` stamps the invisible `out` start state and creates a
 * `{ once: true }` ScrollTrigger at BIND time. A trigger created when the page
 * is already scrolled beyond its start range is not guaranteed to fire
 * `onEnter` — so the element would sit at `opacity: 0` forever. That happens in
 * three real situations, all of which this section (2.2, below the fold) hits:
 * browser scroll restoration, a `#fragment` deep link, and a
 * `shopify:section:load` re-render of a section the merchant has scrolled to.
 *
 * This is a plain geometry question — `getBoundingClientRect()` against the
 * resolved `start` marker — so it needs no plugin and no second animation
 * library (AD-5/AD-13). ScrollTrigger's own semantics: the start is reached
 * when the element's start point rises to or above the viewport's start marker.
 *
 * Conservative by construction — an unparseable `start` returns false, which
 * keeps the pre-2.2 behaviour (create the trigger) rather than guessing.
 *
 * Takes a pre-measured `rect` rather than an element (perf pass, 2026-08-25):
 * `initScrollReveals` used to call `el.getBoundingClientRect()` from inside a
 * loop that ALSO writes `dataset`/`style` on each element it visits, so every
 * iteration after the first forced a synchronous layout flush off the
 * previous iteration's write — a Slow-4G/4x-CPU trace attributed 29ms to this
 * function and named the whole loop as the largest single reflow trigger on
 * the page. The caller now reads every element's rect in one batched pass
 * BEFORE writing anything, so this function only ever does arithmetic.
 *
 * @param {DOMRect|null} rect - `el.getBoundingClientRect()`, read by the caller
 *   before any DOM writes in its own pass
 * @param {string} start - a ScrollTrigger start string, e.g. 'top 85%'
 * @returns {boolean}
 */
function isPastStart(rect, start) {
  if (typeof start !== 'string') return false;
  if (!rect) return false;
  const parts = start.trim().toLowerCase().split(/\s+/);
  if (parts.length !== 2) return false;

  // display:none (or otherwise unlaid-out) at bind time yields an all-zero rect, which would
  // make every offset resolve to 0 and the comparison below spuriously true — decline to guess,
  // same as an unparseable `start` (conservative-by-construction, see doc comment above).
  if (rect.width === 0 && rect.height === 0) return false;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const elementOffset = resolveStartOffset(parts[0], rect.height);
  const viewportOffset = resolveStartOffset(parts[1], viewportHeight);
  if (elementOffset === null || viewportOffset === null) return false;

  return rect.top + elementOffset <= viewportOffset;
}

/**
 * Scroll-reveal helper (AD-5/AD-6).
 *
 * Reveals elements matching `selector` as they enter the viewport, bound to
 * NATIVE scroll — no scroller proxy, no pin, no scrub.
 *
 * `IntersectionObserver` DETECTS; CSS PAINTS (Story 2.1 fix, extended
 * 2026-08-04; GSAP ScrollTrigger's detection swapped for native
 * `IntersectionObserver` 2026-08-25 — see the module header). This helper
 * does not tween anything. It stamps `data-reveal-state="out"` when it binds
 * an element and flips it to `"in"` on entry; `assets/critical.css` owns
 * the actual opacity / transform transition. Two reasons this is the correct
 * shape here:
 *
 *  - AD-5 says CSS owns fades — JS is for scroll-BINDING, not for painting a
 *    two-keyframe fade CSS does natively on the compositor.
 *  - GSAP's ScrollTrigger, when this was written, was doing that binding job
 *    for exactly one thing across the whole theme: flipping this attribute.
 *    `IntersectionObserver` detects the identical scroll position natively,
 *    for free, so the library added nothing this rewrite couldn't do without
 *    it (measured: ~7,000ms of CPU time across `gsap.js` +
 *    `gsap-scrolltrigger.js` on the live homepage, for one attribute flip).
 *
 * What the client got (2026-08-04). "One fade, everywhere, all at once" was
 * read as cheap. The split above is kept exactly as-is — it is still ONLY
 * detection here — and the richness is added as two more DECLARATIONS that CSS
 * consumes, not as JS animation:
 *
 *  - `data-reveal-role` — `text` | `media` | `card`, taken from the attribute's
 *    own value when a section declares one, otherwise inferred from content
 *    (see `inferRevealRole`). CSS gives each role its own distance, scale,
 *    duration and curve, so a headline, a photograph and a product card no
 *    longer move identically.
 *  - `--nrl-reveal-delay` — one inline custom property carrying this element's
 *    place in its sibling group × 80ms (capped). A row of cards therefore
 *    settles in sequence instead of snapping in as one slab. It is expressed
 *    as a custom property rather than a JS-driven timeline precisely so the
 *    paint stays in CSS (AD-5) — and so a section that hard-codes its own
 *    `transition-delay` (micro-collection-tiles, trust-bar) simply out-specifies
 *    it and keeps its hand-tuned rhythm.
 *
 * Both are compositor-safe by construction: JS writes no geometry, and the only
 * animated properties remain `opacity`/`transform` in critical.css (AD-15).
 * Still no scroller proxy, no pin, no scrub, no smooth-scroll library (AD-6) —
 * page scroll stays native, which is also what keeps the horizontal carousels'
 * `scroll-snap` behaviour intact.
 *
 * Content is never gated behind JS: the start state is applied ONLY once this
 * helper has bound the element, so with JS disabled (or before it runs) no
 * `data-reveal-state` exists and the element is simply visible.
 *
 * Reduced motion: the element is stamped `"in"` immediately — end state, no
 * transition — and no observer is created at all. It is also given NO
 * stagger delay, because a delay is motion the user asked not to have; the role
 * attribute is harmless (the `in` state is identical for all three roles) but is
 * skipped too, so the reduced-motion path writes exactly one attribute.
 *
 * Idempotence (Story 2.1). Each element this helper wires is flagged with
 * `data-reveal-bound`, and re-running the helper skips already-flagged elements —
 * so a second call (e.g. the `shopify:section:load` re-init below) can safely
 * rescan overlapping DOM without stacking a duplicate observer. The flag and
 * the state attribute are cleared by the returned `revert()` (called on
 * teardown, and also re-run whenever the live `prefers-reduced-motion` value
 * changes — see below), so a torn-down call leaves the elements re-bindable
 * rather than permanently inert — and, critically, leaves them VISIBLE rather
 * than stuck at the "out" start state.
 *
 * Live reduced-motion changes (parity with the GSAP-era `gsap.matchMedia()`
 * this replaced): that API re-ran its callback whenever the OS-level
 * `prefers-reduced-motion` setting flipped WHILE the page was open, not only
 * at bind time. This rewrite keeps that by watching the same media query
 * itself (`window.matchMedia(REDUCE_MOTION)` + a `change` listener) and
 * re-binding on every flip — teardown-then-rebind, same as the GSAP context's
 * own revert-then-recreate cycle.
 *
 * @param {string|Element|Element[]} [selector='[data-reveal]'] - elements to reveal
 * @param {object} [options] - { start, root }
 * @param {string} [options.start='top 88%'] - the scroll-trigger start position
 * @param {ParentNode} [options.root=document] - scope a string selector to a subtree
 * @returns {{revert: () => void}} call `.revert()` to tear down
 */
export function initScrollReveals(selector = '[data-reveal]', options = {}) {
  const root = options.root ?? document;
  const start = options.start ?? DEFAULT_REVEAL_START;

  let cleanup = null;

  function bind(reduceMotion) {
    const els = Array.from(
      typeof selector === 'string' ? root.querySelectorAll(selector) : selector
    ).filter((el) => !el.dataset.revealBound);

    const observers = [];

    // Stagger index per group, counted across THIS pass only. Keyed by the
    // shared parent `revealGroupParent()` resolves to, so tiles in one grid,
    // items in one trust row and cards in one carousel track each get 0,1,2…
    // while an unrelated element elsewhere restarts at 0. A Map (not a
    // WeakMap) because it lives only for the duration of this call.
    const groupIndex = new Map();

    // ── Batch every geometry read before any write (perf pass, 2026-08-25) ──
    // See isPastStart()'s own doc comment for the measured cost of NOT doing
    // this: reading `getBoundingClientRect()` on element N, after element
    // N-1's iteration already wrote `dataset`/`style`, forces a synchronous
    // layout flush on every single element past the first. Reduced-motion
    // needs no rect at all, so that case is skipped here for free.
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const rects = reduceMotion ? null : els.map((el) => el.getBoundingClientRect());

    // Elements that still need an observer, grouped by the `rootMargin` string
    // `startToRootMargin` computes for them (see that helper's doc comment —
    // in this theme every element resolves to the SAME margin, since only
    // `DEFAULT_REVEAL_START` is ever used, so this is one group / one
    // IntersectionObserver in practice, same shape as before).
    const marginGroups = new Map();

    els.forEach((el, i) => {
      el.dataset.revealBound = 'true';

      if (reduceMotion) {
        // End state immediately — no start state, no role, no delay, no
        // observer, nothing to animate.
        el.dataset.revealState = 'in';
        return;
      }

      // Already scrolled past its start at bind time (Story 2.2)? Then there is nothing left
      // to reveal — stamp the END state and create NO observer, exactly like the reduced-motion
      // branch above but keyed on scroll position instead of the media query. Without this the
      // element would be stamped `out` and wait for an entry that an observer created beyond its
      // start range is not guaranteed to fire (and, for an element already scrolled ABOVE the
      // viewport at bind time, definitely will not fire — it is not intersecting, so a naive
      // IntersectionObserver would leave it invisible forever). See isPastStart() above for the
      // three real ways an element arrives at bind time already past its start.
      //
      // No role and no delay here either, for the same reason as reduced motion: the element
      // goes straight to the end state, so there is no `out → in` transition for either to
      // describe, and a stagger delay on a reveal nobody watched would only be a risk.
      if (isPastStart(rects[i], start)) {
        el.dataset.revealState = 'in';
        return;
      }

      // Which of the three CSS treatments this element gets (AD-5: this is a
      // classification, not an animation — critical.css still paints).
      el.dataset.revealRole = inferRevealRole(el);

      // Its beat in the group's sequence. Written as a custom property so the
      // stagger stays declarative and CSS keeps ownership of the timing.
      const group = revealGroupParent(el);
      const index = group ? (groupIndex.get(group) ?? 0) : 0;
      if (group) groupIndex.set(group, index + 1);
      if (index > 0) {
        const delay = Math.min(index * REVEAL_STAGGER_STEP, REVEAL_STAGGER_MAX);
        el.style.setProperty('--nrl-reveal-delay', `${delay}s`);
      }

      // Start state is applied only NOW, i.e. only once JS is confirmed running.
      el.dataset.revealState = 'out';

      // `rootMargin` computed ONCE here, at bind time, from the already-measured
      // rect — never inside the observer callback (which fires mid-scroll; see
      // `deferToFrame`'s own doc comment below for why a geometry read there is
      // exactly the regression this module's last perf pass eliminated).
      const rootMargin = startToRootMargin(start, rects[i].height, viewportHeight) ?? DEFAULT_ROOT_MARGIN;
      if (!marginGroups.has(rootMargin)) marginGroups.set(rootMargin, []);
      marginGroups.get(rootMargin).push(el);
    });

    marginGroups.forEach((groupEls, rootMargin) => {
      // Bound to native scroll — no `scroller`, no `pin`, no `scrub` (AD-6).
      // `threshold: 0` (the default) fires as soon as any part of the target
      // enters the margin-adjusted root, which — per `startToRootMargin`'s own
      // doc comment — is the element's top edge, matching ScrollTrigger's own
      // `top X%` semantics exactly.
      const observer = new IntersectionObserver(
        (entries, obs) => {
          // No geometry reads here — only the write this helper exists to make,
          // plus unobserving (the `{ once: true }` equivalent).
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.dataset.revealState = 'in';
            obs.unobserve(entry.target);
          });
        },
        { rootMargin }
      );
      groupEls.forEach((el) => observer.observe(el));
      observers.push(observer);
    });

    // Runs on teardown (media-query flip or explicit `.revert()`): disconnect
    // every observer and release EVERYTHING this pass wrote, so the next bind
    // re-binds these elements from a clean slate and nothing is left stranded
    // at the invisible start state. The role attribute and the inline delay
    // must be released along with the two state flags — a stale
    // `--nrl-reveal-delay` surviving a re-bind would hold a re-revealed element
    // back for no reason, and a stale role would out-rank the one the next
    // pass infers.
    return () => {
      observers.forEach((o) => o.disconnect());
      els.forEach((el) => {
        delete el.dataset.revealBound;
        delete el.dataset.revealState;
        delete el.dataset.revealRole;
        el.style.removeProperty('--nrl-reveal-delay');
      });
    };
  }

  const mql = window.matchMedia(REDUCE_MOTION);

  function run() {
    cleanup?.();
    cleanup = bind(mql.matches);
  }

  run();

  // Live-updates on an OS-level reduced-motion flip, mirroring the GSAP-era
  // `gsap.matchMedia()` this replaced (see doc comment above). `addListener`
  // is the deprecated-but-still-necessary fallback for Safari < 14.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', run);
  } else if (typeof mql.addListener === 'function') {
    mql.addListener(run);
  }

  return {
    revert() {
      cleanup?.();
      cleanup = null;
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', run);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(run);
      }
    },
  };
}

/**
 * Poster-first video helper (AD-7).
 *
 * Contract — a container carries the poster and the deferred video source:
 *
 *   <div data-poster-video data-video-src="{{ 'hero.mp4' | asset_url }}">
 *     <img src="poster.avif" fetchpriority="high" width=… height=… alt="…">
 *   </div>
 *
 * The <img> poster is the LCP element (the section markup owns its preload /
 * fetchpriority / dimensions per AD-7 & AD-15). This helper does NOT touch the
 * poster; it only, AFTER first render, lazily injects a muted inline
 * autoplay-loop <video> once the container nears the viewport. Under
 * reduced-motion it injects NOTHING — poster only, no video byte fetched.
 *
 * Failure path (Story 2.1). A merchant's video can 404 or be undecodable. The
 * injected `<video>` gets a one-shot `error` listener that REMOVES it, restoring
 * the poster as the visible state, and stamps the container `data-video-failed`
 * so nothing retries that source in a loop.
 *
 * @param {ParentNode} [root=document] - scope to search for containers
 * @returns {{disconnect: () => void}|null} handle to stop observing (null when
 *   nothing was wired), so a section re-render can tear down its own observer.
 */
export function initPosterVideos(root = document) {
  // AD-7: reduced-motion ⇒ poster only, never load the video.
  if (prefersReducedMotion()) return null;

  const containers = Array.from(root.querySelectorAll('[data-poster-video]'));
  if (!containers.length) return null;

  const loadVideo = (container) => {
    // Re-check live: the user may have enabled reduced-motion after boot,
    // between the initial scan and this container intersecting the viewport.
    if (prefersReducedMotion()) return;
    if (container.querySelector('video')) return; // already loaded
    if (container.dataset.videoFailed) return; // this source already failed — no retry loop
    const src = container.dataset.videoSrc;
    if (!src) return;

    const video = document.createElement('video');
    video.src = src;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true'); // decorative; poster carries meaning
    const poster = container.querySelector('img');
    if (poster?.currentSrc) video.poster = poster.currentSrc;

    // A bad/undecodable source must not leave a dead <video> stacked over the
    // poster: drop the element so the poster is the visible state again.
    video.addEventListener(
      'error',
      () => {
        container.dataset.videoFailed = 'true';
        video.remove();
      },
      { once: true }
    );

    container.appendChild(video);
    // Some browsers need an explicit play() for programmatically-added videos.
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  let io = null;
  let cancelled = false;

  const observe = () => {
    if (cancelled) return;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadVideo(entry.target);
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '200px' } // start fetching just before it scrolls in
      );
      containers.forEach((c) => io.observe(c));
    } else {
      // No IO support — load them once, still after first render.
      containers.forEach(loadVideo);
    }
  };

  // "After first render": defer to idle, falling back to a post-load task.
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(observe, { timeout: 2000 });
  } else if (document.readyState === 'complete') {
    // `load` has ALREADY fired, so listening for it would never run. This is the
    // state of every `shopify:section:load` re-init — i.e. the whole Theme Editor
    // path — on engines without requestIdleCallback (Safari/iOS ≤18.3, which is
    // also the primary `playsinline` target). Without this branch the observer is
    // never created and the video is silently never injected.
    setTimeout(observe, 0);
  } else {
    window.addEventListener('load', () => setTimeout(observe, 0), { once: true });
  }

  return {
    disconnect() {
      cancelled = true;
      io?.disconnect();
      io = null;
    },
  };
}

/**
 * Per-root wiring registry (Story 2.1; keying corrected in review).
 *
 * Keyed by the ROOT ELEMENT ITSELF, not by a section id string. Two earlier
 * approaches were wrong:
 *  - `event.detail.sectionId` ("motion_hero") and `element.id`
 *    ("shopify-section-motion_hero") are different strings for the same section,
 *    so a boot-time entry could never be matched by an unload-time lookup.
 *  - `element.id` is `''` (not nullish) when unset, so `??` would let every
 *    id-less section collide on a single `''` key.
 * Element identity has neither problem. A WeakMap is used so that if Shopify
 * ever fails to fire `unload`, the detached section — and its observer and
 * matchMedia context — can still be collected rather than pinned forever.
 */
const sectionMotion = new WeakMap();

function teardownRoot(root) {
  if (!root) return;
  const wired = sectionMotion.get(root);
  if (!wired) return;
  wired.reveals?.revert();
  wired.videos?.disconnect();
  sectionMotion.delete(root);
}

/** Tear down anything previously wired for `root`, then wire it fresh. */
function wireRoot(root) {
  if (!root) return;
  teardownRoot(root);
  sectionMotion.set(root, {
    reveals: initScrollReveals('[data-reveal]', { root }),
    videos: initPosterVideos(root),
  });
}

/**
 * Baseline auto-init. Wires the shared reveal + poster-video behaviours for any
 * section that opts in with `[data-reveal]` / `[data-poster-video]`. With no
 * such elements present (the state at Story 1.6), this is a no-op — the module
 * simply loads and waits.
 *
 * Wiring is done PER SECTION rather than once over the whole document (fixed in
 * Story 2.1 review). Both helpers return teardown handles, and a document-wide
 * handle cannot be torn down for one section — so the previous version, which
 * discarded boot's handles entirely, left boot's IntersectionObserver observing
 * detached containers and boot's ScrollTriggers pointing at detached elements on
 * every Theme Editor edit. Per-section wiring is what actually makes the
 * `shopify:section:*` teardown below reach the first-paint pass, which is the
 * only pass a merchant's FIRST edit ever hits.
 *
 * Sections opt in via their own markup (docs/js-contracts.md), so `.shopify-section`
 * is the correct granularity; the `document` fallback covers a page that somehow
 * ships opt-in markup outside any section.
 */
/**
 * The IntersectionObserver behind the lazy boot path below. Held at module
 * scope (not a closure inside `boot()`) so the `shopify:section:unload`
 * handler can `unobserve()` a section that gets removed before it was ever
 * wired — otherwise a stale entry would sit in the observer forever.
 */
let bootObserver = null;

/**
 * Wire every current `.shopify-section` — the pre-2026-08-25 behaviour, kept
 * as the fallback for browsers without `IntersectionObserver`.
 */
function wireAllSections(sections) {
  sections.forEach(wireRoot);
}

/**
 * Defer `fn` to just before the next paint, never running it synchronously in
 * the caller's own frame. Falls back to `setTimeout` when `requestAnimationFrame`
 * is unavailable (SSR-style test harnesses; no real browser lacks rAF).
 *
 * Why this exists (perf pass, 2026-08-25 follow-up — see the regression note in
 * `boot()`'s own doc comment below): an `IntersectionObserver` callback fires
 * WHILE THE PAGE IS ACTIVELY SCROLLING — that is the entire point of a
 * rootMargin-based observer. Doing `initScrollReveals`' read/write pass
 * synchronously inside that callback means the read/write happens on top of a
 * scroll frame whose layout the browser has not necessarily settled, and any
 * OTHER geometry read that happens to land in the same task (a ResizeObserver
 * callback elsewhere, an image finishing decode) inherits that dirty state and
 * pays for a forced synchronous layout it did not itself cause. Moving the work
 * one frame later, via rAF, decouples it from the observer callback's own
 * timing: it still runs almost immediately (next paint), but as ordinary
 * rendering-pipeline work rather than as a side effect nested inside a
 * scroll-driven callback.
 */
function deferToFrame(fn) {
  if ('requestAnimationFrame' in window) {
    window.requestAnimationFrame(fn);
  } else {
    setTimeout(fn, 0);
  }
}

/**
 * Hybrid per-section boot (perf pass, 2026-08-25; revised same day after
 * measurement — see below).
 *
 * `boot()` used to call `wireRoot` on every `.shopify-section` synchronously,
 * in one pass, at `DOMContentLoaded` — which means `initScrollReveals` ran
 * its full read/write pass (see above) for every `[data-reveal]` element on
 * the ENTIRE page, sections far below the fold included, all inside one
 * top-level task. A Slow-4G/4x-CPU trace of the live homepage named exactly
 * that anonymous top-level frame as the single largest reflow trigger on the
 * page, and it also means GSAP's ScrollTrigger registers and measures every
 * trigger on the page up front (the 550ms `gsap-scrolltrigger.js` cost this
 * pass was asked to reduce) rather than only the ones a shopper is actually
 * near.
 *
 * The FIRST cut of this fix wired every section lazily, on an
 * IntersectionObserver with `rootMargin: '800px 0px'`, and it made the
 * anonymous top-level-task reflow smaller — but a re-trace showed the
 * observer's own callback had become the single LARGEST reflow trigger on the
 * page, and two unrelated call frames that also read geometry
 * (`carousel.js get maxScroll`, `auto-rail.js fill`) got 3-4x MORE expensive,
 * not less. The mechanism: on a slow connection the page is still loading
 * (and a shopper can already be scrolling) when this module's sections start
 * crossing the 800px trigger line, so `wireRoot`'s read/write pass was running
 * DURING active scroll instead of at the one quiet moment `DOMContentLoaded`
 * used to guarantee — and a measurement taken while scroll-driven layout is
 * still settling costs far more than the identical measurement taken at idle,
 * for this call frame and for whatever else's geometry read happens to share
 * the task.
 *
 * Two changes fix the mechanism rather than trade one cost for another:
 *
 *  1. HYBRID eager/lazy split. A section already inside, or within one screen
 *     height of, the viewport AT BOOT TIME is wired immediately and
 *     synchronously, exactly like the pre-lazy behaviour — because `boot()`
 *     itself runs at `DOMContentLoaded`/script-eval time, which IS the quiet
 *     moment (the shopper cannot have scrolled before the page has finished
 *     loading enough to run this script). Only sections genuinely far below
 *     the fold are deferred to the observer, so the expensive "wire everything
 *     up front" cost this whole effort exists to avoid is paid for a handful
 *     of above-the-fold sections, not all of them.
 *  2. rAF-DEFERRED lazy wiring. Every section the observer still handles is
 *     wired via `deferToFrame()` (above), not inside the callback itself — so
 *     even a section that crosses the trigger line mid-scroll has its
 *     read/write pass land on the NEXT animation frame rather than nested
 *     inside the scroll-driven callback that discovered it.
 *
 * A section is unobserved the moment it starts intersecting (before the
 * deferred wire runs), so re-entering the viewport later, or being wired twice
 * by a fast double-fire, cannot happen. `entry.target.isConnected` is
 * re-checked inside the deferred callback because a Theme Editor edit (or,
 * in principle, an app) could remove the section between the intersection and
 * the next frame; `wireRoot` on a detached node would still "work" (it only
 * touches the node's own subtree) but there is nothing to gain from wiring
 * something no longer in the document, and `shopify:section:unload` already
 * unobserves before this could race in practice — this is a second, cheap
 * guard, not the load-bearing one.
 *
 * `motion.js` is still the one eagerly-loaded, critical-path module
 * (`layout/theme.liquid`), so this does not defer loading the module itself —
 * only the DOM work each section's reveal wiring costs.
 */
function boot() {
  const sections = document.querySelectorAll('.shopify-section');
  if (!sections.length) {
    wireRoot(document);
    return;
  }

  if (!('IntersectionObserver' in window)) {
    wireAllSections(sections);
    return;
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  // Batch every geometry read before any write, same discipline as
  // initScrollReveals' own rect pass: reading each section's rect one at a
  // time interleaved with `wireRoot`'s writes would force a reflow per
  // section instead of one flush for the whole boot pass.
  const rects = Array.from(sections, (section) => section.getBoundingClientRect());
  const lazySections = [];

  sections.forEach((section, i) => {
    // "Near the fold" — inside the viewport already, or at most one more
    // screen height below it. Boot time is quiet (see doc comment above), so
    // wiring these now costs nothing a shopper can perceive as jank, and it
    // guarantees the sections they see first are never mid-scroll-deferred.
    if (rects[i].top < viewportHeight * 2) {
      wireRoot(section);
    } else {
      lazySections.push(section);
    }
  });

  if (!lazySections.length) return;

  bootObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        deferToFrame(() => {
          if (!entry.target.isConnected) return;
          wireRoot(entry.target);
        });
      });
    },
    { rootMargin: '800px 0px' }
  );
  lazySections.forEach((section) => bootObserver.observe(section));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

/**
 * Dynamic re-init (Story 2.1 — the module's first real consumer).
 *
 * `boot()` scans the DOM once. A section re-rendered by the Theme Editor or the
 * Section Rendering API replaces its markup with nodes that boot never saw, so
 * without this hook a merchant editing a hero setting gets a section whose
 * poster-video and reveals are dead. Shopify fires `shopify:section:unload` /
 * `shopify:section:load` on `document` around every such re-render, so we tear
 * down exactly what we created for that section and re-wire the new subtree,
 * scoped to `event.target` (the `.shopify-section` wrapper).
 *
 * Idempotent by construction: `initPosterVideos` skips containers that already
 * hold a `<video>` (and ones flagged `data-video-failed`), `initScrollReveals`
 * skips elements flagged `data-reveal-bound`, and `wireRoot` tears down before it
 * re-wires — so nothing accumulates even if `unload` never fired.
 */
document.addEventListener('shopify:section:unload', (event) => {
  // A section removed before it ever intersected the viewport was never
  // wired by `wireRoot` — but it may still be sitting inside `bootObserver`,
  // observed and waiting. `unobserve` is a no-op if it already fired (and
  // therefore already unobserved itself), so this is safe unconditionally.
  bootObserver?.unobserve(event.target);
  teardownRoot(event.target);
});

document.addEventListener('shopify:section:load', (event) => {
  // If this section is still sitting unwired inside `bootObserver` (never
  // scrolled near), stop waiting for it — it is about to be wired directly.
  bootObserver?.unobserve(event.target);
  wireRoot(event.target);
});

export default { initScrollReveals, initPosterVideos, prefersReducedMotion, REDUCE_MOTION };
